import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import IssueDetail from "./IssueDetail";

/**
 * Shared Jira-style Issue Detail Modal used across the entire Project Management module.
 *
 * Provides:
 * - Centered 88-92vw / 88-90vh responsive modal with dark translucent backdrop (rgba(9, 30, 66, 0.54)).
 * - 2-column desktop layout (main content on left, properties on right).
 * - Compact top header with parent/child breadcrumbs, watchers, share link, more menu, full page link, and close X.
 * - In-place navigation when clicking subtasks, linked issues, epic children, and parent issues.
 * - Restores background scrolling upon close.
 * - High stacking z-index (1300) above all board columns, navbars, and cards.
 */
const IssueDetailModal = ({
  issueKey,
  open = false,
  onClose,
  onChanged,
  onOpenIssue,
  initialAction = null,
}) => {
  const [currentKey, setCurrentKey] = useState(issueKey);

  // Synchronize current key when issueKey prop changes
  useEffect(() => {
    if (issueKey) {
      setCurrentKey(issueKey);
    }
  }, [issueKey]);

  // Handle in-modal issue navigation (e.g. clicking sub-task or parent breadcrumb)
  const handleOpenIssue = (newKey) => {
    if (!newKey) return;
    setCurrentKey(newKey);
    onOpenIssue?.(newKey);
  };

  const activeKey = currentKey || issueKey;
  const isOpen = Boolean(open && activeKey);

  return (
    <Dialog
      open={isOpen}
      onClose={(e, reason) => {
        onClose?.();
      }}
      maxWidth={false}
      scroll="paper"
      BackdropProps={{
        sx: {
          bgcolor: "rgba(9, 30, 66, 0.54)",
          backdropFilter: "blur(2px)",
          zIndex: 1299,
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "96vw", sm: "92vw", md: "88vw" },
          maxWidth: 1320,
          height: { xs: "94vh", sm: "90vh", md: "88vh" },
          maxHeight: "90vh",
          borderRadius: "12px",
          boxShadow: "0 20px 48px -12px rgba(9, 30, 66, 0.35), 0 0 1px rgba(9, 30, 66, 0.3)",
          border: "1px solid #DFE1E6",
          bgcolor: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          m: 0,
          zIndex: 1300,
        },
      }}
    >
      {activeKey && (
        <IssueDetail
          key={activeKey}
          issueKey={activeKey}
          embedded
          inModal
          initialAction={initialAction}
          onClose={onClose}
          onChanged={onChanged}
          onOpenIssue={handleOpenIssue}
        />
      )}
    </Dialog>
  );
};

export default IssueDetailModal;
